import { Grid, useTheme } from '@mui/material'
import { palette, C1Button } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { CreateReturn, Status } from 'lib/rpc'
import { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

interface DropzoneOptions {
    onDrop(files: File[])

    createReturn: CreateReturn<unknown>
}

export const Dropzone = ({ onDrop, createReturn }: DropzoneOptions): React.ReactElement => {
    const theme = useTheme()
    const [isHover, setIsHover] = useState<boolean>(false)

    const chooseFileInput = useRef<HTMLInputElement | null>(null)

    const drop = (files: File[]): void => {
        setIsHover(false)
        onDrop(files)
    }

    const dropHandler = (ev): void => {
        ev.preventDefault()

        const files: File[] = []
        if (ev.dataTransfer.items) {
            const items = ev.dataTransfer.items
            // Use DataTransferItemList interface to access the file(s)
            for (let i = 0; i < items.length; i++) {
                // If dropped items aren't files, reject them
                if (items[i].kind === 'file') {
                    const file = items[i].getAsFile()
                    files.push(file)
                }
            }
        } else {
            // Use DataTransfer interface to access the file(s)
            for (let i = 0; i < ev.dataTransfer.files.length; i++) {
                const file = ev.dataTransfer.files[i]
                files.push(file)
            }
        }

        drop(files)
    }

    const fileSelectedHandler = (): void => {
        if (!chooseFileInput?.current?.files) {
            return
        }
        const files: File[] = []
        const inputFiles = chooseFileInput.current.files
        for (let i = 0; i < (inputFiles?.length || 0); i++) {
            const file = inputFiles.item(i)
            if (!file) {
                continue
            }
            files.push(file)
        }
        drop(files)
    }

    return (
        <div
            onDrop={dropHandler}
            onDragOver={(ev): void => {
                ev.preventDefault()
                setIsHover(true)
            }}
            onDragExit={(ev): void => {
                ev.preventDefault()
                setIsHover(false)
            }}
        >
            <Grid
                container
                direction="column"
                alignItems="center"
                style={{
                    border: `2px dashed ${palette.border.primary}`,
                    borderRadius: '6px',
                    width: '100%',
                    height: '100%',
                    background: isHover ? palette.grey[50] : '',
                }}
            >
                <div
                    style={{
                        margin: theme.spacing(6),
                    }}
                >
                    {createReturn.status === Status.LOADING && <C1CircularProgress />}
                    {!isHover && createReturn.status !== Status.LOADING && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Grid item pb={1}>
                                <C1Button
                                    variant="contained"
                                    color="primary"
                                    onClick={(): void => {
                                        if (chooseFileInput.current) chooseFileInput.current.click()
                                    }}
                                >
                                    <FormattedMessage
                                        id="components_dropzone_choose_file"
                                        defaultMessage="Choose file"
                                    />
                                    <input
                                        style={{ display: 'none' }}
                                        ref={chooseFileInput}
                                        id="choose-file-input"
                                        type="file"
                                        onInput={fileSelectedHandler}
                                    />
                                </C1Button>
                            </Grid>
                            <Grid item>
                                <FormattedMessage
                                    id="components_dropzone_choose_file"
                                    defaultMessage="or drop a file here"
                                />
                            </Grid>
                        </div>
                    )}
                    {isHover && (
                        <Grid item>
                            <FormattedMessage id="components_dropzone_choose_file" defaultMessage="Drop file here" />
                        </Grid>
                    )}
                </div>
            </Grid>
        </div>
    )
}
